import { useRef, useEffect, useCallback } from 'react';

type PollingCallback = () => Promise<boolean>;

export function useChatPolling(
    callback: PollingCallback,
    initialInterval = 15000,
    maxInterval = 120000,
    isEnabled = true
) {
    const intervalRef = useRef(initialInterval);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    // Ref para scheduleNextPoll — permite resetPolling usar sem dependência circular
    const scheduleNextPollRef = useRef<(() => void) | null>(null);

    // Função para resetar o polling (usar quando houver interação do usuário ou evento realtime)
    const resetPolling = useCallback(() => {
        intervalRef.current = initialInterval;
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        // Só reagenda — não dispara fetch imediato (evita piscar a UI)
        scheduleNextPollRef.current?.();
    }, [initialInterval]);

    useEffect(() => {
        if (!isEnabled) return;

        let isCancelled = false;
        let isPolling = false;
        intervalRef.current = initialInterval;

        const scheduleNextPoll = () => {
            if (isCancelled || isPolling) return;
            if (timeoutRef.current) clearTimeout(timeoutRef.current);

            timeoutRef.current = setTimeout(async () => {
                if (isCancelled) return;

                // OPT-3: Pula polling se aba está oculta
                if (document.visibilityState !== 'visible') {
                    scheduleNextPoll();
                    return;
                }

                isPolling = true;
                try {
                    const hasChanges = await callbackRef.current();

                    if (isCancelled) return;

                    // Backoff exponencial: se não houve mudanças, aumenta o intervalo
                    // Se houve mudanças, reseta para inicial
                    if (hasChanges) {
                        intervalRef.current = initialInterval;
                    } else {
                        intervalRef.current = Math.min(intervalRef.current * 2, maxInterval);
                    }
                } catch (error) {
                    if (!isCancelled) console.error('[useChatPolling] Erro no polling:', error);
                } finally {
                    isPolling = false;
                }

                scheduleNextPoll();
            }, intervalRef.current);
        };

        // Registra ref para que resetPolling possa reagendar
        scheduleNextPollRef.current = scheduleNextPoll;

        scheduleNextPoll();

        return () => {
            isCancelled = true;
            scheduleNextPollRef.current = null;
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [isEnabled, initialInterval, maxInterval]);

    return { resetPolling };
}
